import React, { useMemo, useState } from 'react';
import particularesApiService from '../services/ParticularesApiService';
import SearchableSelect from './common/SearchableSelect';
import ParticularModal from './common/ParticularModal';

// "Modo colegio": años y divisiones de la cuenta particular. Cada división tiene sus cursos (un curso pertenece
// a una sola división) y sus preceptores (se asignan desde la ficha del preceptor).
// Los años vienen del dashboard (anios/onAniosChange) porque también los usa el formulario del preceptor.

const getValue = (source, lower, upper) => source?.[lower] ?? source?.[upper];
const getId = (item) => String(getValue(item, 'id', 'Id') || '');
const getList = (source, lower, upper) => {
  const value = getValue(source, lower, upper);
  return Array.isArray(value) ? value : [];
};

const EMPTY_ANIO = { id: '', nombre: '', orden: '' };
const EMPTY_DIVISION = { id: '', idAnio: '', nombre: '', cursoIds: [] };

const ParticularDivisiones = ({ anios, onAniosChange, cursos, onCursosChanged, puedeGestionar }) => {
  const [anioForm, setAnioForm] = useState(null);
  const [divisionForm, setDivisionForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  // División actual de cada curso (para avisar que asignarlo acá lo mueve de su división anterior).
  const divisionPorCurso = useMemo(() => {
    const map = new Map();
    anios.forEach((anio) => getList(anio, 'divisiones', 'Divisiones').forEach((division) => {
      getList(division, 'cursos', 'Cursos').forEach((curso) => map.set(getId(curso), division));
    }));
    return map;
  }, [anios]);

  // Etiquetas con color de cada curso (el listado de la división trae solo los nombres).
  const etiquetasPorCurso = useMemo(() => new Map(cursos.map((curso) => [
    getId(curso),
    getList(curso, 'etiquetas', 'Etiquetas').map((etiqueta) => ({
      nombre: getValue(etiqueta, 'nombre', 'Nombre') || '',
      color: getValue(etiqueta, 'color', 'Color') || ''
    }))
  ])), [cursos]);

  const getEtiquetasCurso = (curso) => etiquetasPorCurso.get(getId(curso))
    || getList(curso, 'etiquetas', 'Etiquetas').map((nombre) => ({ nombre: String(nombre), color: '' }));

  const cursoOptions = useMemo(() => cursos.map((curso) => {
    const actual = divisionPorCurso.get(getId(curso));
    const enOtra = actual && getId(actual) !== divisionForm?.id;
    return {
      value: getId(curso),
      label: getValue(curso, 'nombre', 'Nombre') || 'Curso',
      secondary: enOtra ? `Ahora en ${getValue(actual, 'nombreCompleto', 'NombreCompleto')}: se moverá a esta división` : '',
      tags: getList(curso, 'etiquetas', 'Etiquetas').map((etiqueta) => ({
        label: getValue(etiqueta, 'nombre', 'Nombre') || '',
        color: getValue(etiqueta, 'color', 'Color') || ''
      }))
    };
  }), [cursos, divisionForm?.id, divisionPorCurso]);

  const anioOptions = useMemo(() => anios.map((anio) => ({
    value: getId(anio),
    label: getValue(anio, 'nombre', 'Nombre') || 'Año'
  })), [anios]);

  const run = async (action, mensaje, { refreshCursos = false } = {}) => {
    setSaving(true);
    setError('');
    try {
      const response = await action();
      onAniosChange(getList(response, 'anios', 'Anios'));
      if (refreshCursos) onCursosChanged?.();
      setAviso(mensaje);
      return true;
    } catch (err) {
      setError(err?.message || 'No pudimos guardar los cambios.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAnio = async (event) => {
    event.preventDefault();
    const payload = { nombre: anioForm.nombre.trim(), orden: anioForm.orden === '' ? null : Number(anioForm.orden) };
    const ok = await run(
      () => (anioForm.id ? particularesApiService.updateAnio(anioForm.id, payload) : particularesApiService.createAnio(payload)),
      anioForm.id ? 'Año actualizado.' : 'Año creado.'
    );
    if (ok) setAnioForm(null);
  };

  const saveDivision = async (event) => {
    event.preventDefault();
    const payload = { idAnio: divisionForm.idAnio, nombre: divisionForm.nombre.trim(), cursoIds: divisionForm.cursoIds };
    const ok = await run(
      () => (divisionForm.id ? particularesApiService.updateDivision(divisionForm.id, payload) : particularesApiService.createDivision(payload)),
      divisionForm.id ? 'División actualizada.' : 'División creada.',
      { refreshCursos: true }
    );
    if (ok) setDivisionForm(null);
  };

  const confirmDelete = async () => {
    const { tipo, item } = deleteTarget;
    const ok = await run(
      () => (tipo === 'anio' ? particularesApiService.deleteAnio(getId(item)) : particularesApiService.deleteDivision(getId(item))),
      tipo === 'anio' ? 'Año eliminado.' : 'División eliminada.',
      { refreshCursos: true }
    );
    if (ok) setDeleteTarget(null);
  };

  const openDivision = (anio, division = null) => {
    setError('');
    setDivisionForm(division ? {
      id: getId(division),
      idAnio: String(getValue(division, 'idAnio', 'IdAnio') || getId(anio)),
      nombre: getValue(division, 'nombre', 'Nombre') || '',
      cursoIds: getList(division, 'cursos', 'Cursos').map(getId)
    } : { ...EMPTY_DIVISION, idAnio: getId(anio) });
  };

  const totalDivisiones = anios.reduce((acc, anio) => acc + getList(anio, 'divisiones', 'Divisiones').length, 0);

  return (
    <section className="content-card particular-section">
      <div className="card-header particular-card-header">
        <div>
          <h2 className="card-title">
            <i className="fas fa-sitemap" />
            Años y divisiones
          </h2>
          <p className="particular-card-subtitle">
            {anios.length} {anios.length === 1 ? 'año' : 'años'} · {totalDivisiones} {totalDivisiones === 1 ? 'división' : 'divisiones'}. Los preceptores se asignan desde su ficha.
          </p>
        </div>
        {puedeGestionar && (
          <div className="particular-toolbar-actions">
            <button type="button" className="particular-primary-btn" onClick={() => { setError(''); setAnioForm(EMPTY_ANIO); }}>
              <i className="fas fa-plus" />
              Nuevo año
            </button>
          </div>
        )}
      </div>

      <div className="card-content">
        {aviso && (
          <div className="particular-division-aviso" role="status">
            <i className="fas fa-check-circle" />
            <span>{aviso}</span>
            <button type="button" onClick={() => setAviso('')} aria-label="Cerrar aviso"><i className="fas fa-times" /></button>
          </div>
        )}
        {error && !anioForm && !divisionForm && !deleteTarget && <div className="particular-matricula-error">{error}</div>}

        {anios.length === 0 ? (
          <div className="particular-empty">
            Todavía no hay años. Creá el primero (por ejemplo, "1° año") y después sus divisiones.
          </div>
        ) : (
          <div className="particular-anios">
            {anios.map((anio) => {
              const divisiones = getList(anio, 'divisiones', 'Divisiones');
              return (
                <article key={getId(anio)} className="particular-anio">
                  <header className="particular-anio-head">
                    <div>
                      <strong>{getValue(anio, 'nombre', 'Nombre')}</strong>
                      <span>{divisiones.length} {divisiones.length === 1 ? 'división' : 'divisiones'}</span>
                    </div>
                    {puedeGestionar && (
                      <div className="particular-anio-actions">
                        <button type="button" className="particular-secondary-btn" onClick={() => openDivision(anio)}>
                          <i className="fas fa-plus" />
                          División
                        </button>
                        <button
                          type="button"
                          className="particular-icon-btn action-edit"
                          title="Editar año"
                          onClick={() => { setError(''); setAnioForm({ id: getId(anio), nombre: getValue(anio, 'nombre', 'Nombre') || '', orden: String(getValue(anio, 'orden', 'Orden') ?? '') }); }}
                        >
                          <i className="fas fa-pen" />
                        </button>
                        <button type="button" className="particular-icon-btn danger" title="Eliminar año" onClick={() => { setError(''); setDeleteTarget({ tipo: 'anio', item: anio }); }}>
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    )}
                  </header>

                  {divisiones.length === 0 ? (
                    <p className="particular-matricula-muted">Sin divisiones.</p>
                  ) : (
                    <div className="particular-divisiones-grid">
                      {divisiones.map((division) => {
                        const cursosDivision = getList(division, 'cursos', 'Cursos');
                        const preceptores = getList(division, 'preceptores', 'Preceptores');
                        return (
                          <div key={getId(division)} className="particular-division-card">
                            <div className="particular-division-card-head">
                              <strong>{getValue(division, 'nombreCompleto', 'NombreCompleto')}</strong>
                              {puedeGestionar && (
                                <div>
                                  <button type="button" className="particular-icon-btn action-edit" title="Editar división" onClick={() => openDivision(anio, division)}>
                                    <i className="fas fa-pen" />
                                  </button>
                                  <button type="button" className="particular-icon-btn danger" title="Eliminar división" onClick={() => { setError(''); setDeleteTarget({ tipo: 'division', item: division }); }}>
                                    <i className="fas fa-trash" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="particular-division-block">
                              <span><i className="fas fa-layer-group" /> Cursos</span>
                              {cursosDivision.length === 0 ? (
                                <small>Sin cursos</small>
                              ) : (
                                <ul className="particular-division-cursos">
                                  {cursosDivision.map((curso) => {
                                    const etiquetas = getEtiquetasCurso(curso);
                                    return (
                                      <li key={getId(curso)}>
                                        <strong>{getValue(curso, 'nombre', 'Nombre')}</strong>
                                        {etiquetas.length > 0 && (
                                          <span className="particular-division-curso-tags">
                                            {etiquetas.map((etiqueta) => (
                                              <span key={etiqueta.nombre} className="particular-tag" style={etiqueta.color ? { '--tag-color': etiqueta.color } : undefined}>
                                                {etiqueta.nombre}
                                              </span>
                                            ))}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                            <div className="particular-division-block">
                              <span><i className="fas fa-user-shield" /> Preceptores</span>
                              {preceptores.length === 0 ? (
                                <small>Sin preceptores</small>
                              ) : (
                                <small>{preceptores.map((preceptor) => getValue(preceptor, 'nombre', 'Nombre')).join(', ')}</small>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      {anioForm && (
        <ParticularModal title={anioForm.id ? 'Editar año' : 'Nuevo año'} icon="fas fa-sitemap" onClose={saving ? () => {} : () => setAnioForm(null)}>
          <form className="particular-modal-body" onSubmit={saveAnio}>
            <div className="particular-modal-grid">
              <label>
                Nombre
                <input value={anioForm.nombre} onChange={(event) => setAnioForm((prev) => ({ ...prev, nombre: event.target.value }))} placeholder='Ej.: "1° año"' maxLength={80} required autoFocus />
              </label>
              <label>
                Orden <small>(opcional)</small>
                <input type="number" min="0" value={anioForm.orden} onChange={(event) => setAnioForm((prev) => ({ ...prev, orden: event.target.value }))} placeholder="Al final" />
              </label>
            </div>
            {error && <div className="particular-matricula-error">{error}</div>}
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={() => setAnioForm(null)} disabled={saving}>Cancelar</button>
              <button type="submit" className="particular-primary-btn" disabled={saving}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Guardar
              </button>
            </div>
          </form>
        </ParticularModal>
      )}

      {divisionForm && (
        <ParticularModal title={divisionForm.id ? 'Editar división' : 'Nueva división'} icon="fas fa-sitemap" onClose={saving ? () => {} : () => setDivisionForm(null)} className="particular-modal-wide">
          <form className="particular-modal-body" onSubmit={saveDivision}>
            <div className="particular-modal-grid">
              <div className="particular-form-group">
                <span>Año</span>
                <SearchableSelect
                  value={divisionForm.idAnio}
                  onChange={(value) => setDivisionForm((prev) => ({ ...prev, idAnio: value }))}
                  options={anioOptions}
                  placeholder="Elegí el año"
                  disabled={saving}
                />
              </div>
              <label>
                División
                <input value={divisionForm.nombre} onChange={(event) => setDivisionForm((prev) => ({ ...prev, nombre: event.target.value }))} placeholder='Ej.: "A"' maxLength={80} required />
              </label>
            </div>
            <div className="particular-form-group">
              <span>Cursos de la división</span>
              <SearchableSelect
                multiple
                values={divisionForm.cursoIds}
                onChange={(values) => setDivisionForm((prev) => ({ ...prev, cursoIds: values }))}
                options={cursoOptions}
                placeholder="Buscar curso por nombre o etiqueta"
                emptyText="No hay cursos cargados."
                noResultsText="No encontramos cursos."
                disabled={saving}
              />
              <small className="particular-matricula-muted">Cada curso pertenece a una sola división: si ya está en otra, se mueve a esta.</small>
            </div>
            {error && <div className="particular-matricula-error">{error}</div>}
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={() => setDivisionForm(null)} disabled={saving}>Cancelar</button>
              <button type="submit" className="particular-primary-btn" disabled={saving || !divisionForm.idAnio}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Guardar
              </button>
            </div>
          </form>
        </ParticularModal>
      )}

      {deleteTarget && (
        <ParticularModal title={deleteTarget.tipo === 'anio' ? 'Eliminar año' : 'Eliminar división'} icon="fas fa-trash" onClose={saving ? () => {} : () => setDeleteTarget(null)}>
          <div className="particular-modal-body">
            <div className="particular-reset-warning">
              <i className="fas fa-exclamation-triangle" />
              <div>
                <strong>
                  ¿Eliminar {deleteTarget.tipo === 'anio' ? getValue(deleteTarget.item, 'nombre', 'Nombre') : getValue(deleteTarget.item, 'nombreCompleto', 'NombreCompleto')}?
                </strong>
                <p>
                  {deleteTarget.tipo === 'anio'
                    ? 'Se eliminan también sus divisiones. Los cursos no se borran: quedan sin división, y los preceptores se desasignan.'
                    : 'Los cursos no se borran: quedan sin división, y sus preceptores se desasignan.'}
                </p>
              </div>
            </div>
            {error && <div className="particular-matricula-error">{error}</div>}
            <div className="particular-modal-footer">
              <button type="button" className="particular-secondary-btn" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancelar</button>
              <button type="button" className="particular-primary-btn danger" onClick={confirmDelete} disabled={saving}>
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
                Eliminar
              </button>
            </div>
          </div>
        </ParticularModal>
      )}
    </section>
  );
};

export default ParticularDivisiones;
